import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import DateRangePicker from '../../components/reports/DateRangePicker';
import { ROUTES } from '../../consts/general';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PAGE_SIZE = 50;

const formatCell = (value, type) => {
    if (value === null || value === undefined || value === '') return '—';
    if (type === 'currency') {
        const number = Number(value);
        return Number.isFinite(number)
            ? number.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
            : '—';
    }
    if (type === 'number') {
        const number = Number(value);
        return Number.isFinite(number) ? number.toLocaleString('en-US') : '—';
    }
    if (type === 'boolean') return value ? 'Yes' : 'No';
    if (type === 'date') {
        const dateValue = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
            ? `${value}T00:00:00`
            : value;
        const date = new Date(dateValue);
        return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
    }
    return String(value);
};

const CatalogTableReport = ({
    marginFromSidebar,
    reportType,
    title,
    description,
    includeStaleHours = false,
    defaultDays = 30,
}) => {
    const navigate = useNavigate();
    const { beginRequest, isLatest } = useAbortableRequest();
    const { defaultStartDate, defaultEndDate } = useMemo(() => {
        const endDate = new Date();
        return {
            defaultEndDate: format(endDate, 'yyyy-MM-dd'),
            defaultStartDate: format(subDays(endDate, defaultDays), 'yyyy-MM-dd'),
        };
    }, [defaultDays]);
    const { startDate, endDate, setDateRange } = useDateRangeParams(
        defaultStartDate,
        defaultEndDate,
    );
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [bucket, setBucket] = useState('');
    const [vendor, setVendor] = useState('');
    const [filterOptions, setFilterOptions] = useState(null);
    const [staleHours, setStaleHours] = useState('48');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const hasBucketFilterOptions = Boolean(filterOptions?.bucket?.length);
    const hasVendorFilterOptions = Boolean(filterOptions?.vendor?.length);

    const buildParams = useCallback((page, pageSize) => {
        const params = new URLSearchParams({
            startDate,
            endDate,
            page: String(page),
            pageSize: String(pageSize),
        });
        if (hasBucketFilterOptions && bucket) params.set('bucket', bucket);
        if (hasVendorFilterOptions && vendor) params.set('vendor', vendor);
        if (includeStaleHours && staleHours.trim()) params.set('staleHours', staleHours.trim());
        return params;
    }, [
        bucket,
        endDate,
        hasBucketFilterOptions,
        hasVendorFilterOptions,
        includeStaleHours,
        staleHours,
        startDate,
        vendor,
    ]);

    const fetchData = useCallback(async () => {
        const { signal, requestId } = beginRequest();
        setIsLoading(true);
        setError(null);

        try {
            const params = buildParams(currentPage, PAGE_SIZE);
            const response = await fetch(`/api/v1/reports/${reportType}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                signal,
            });
            const responseData = await response.json().catch(() => ({}));
            if (!response.ok) {
                const message = responseData.error || 'Failed to fetch report data';
                throw new Error(response.status === 403 ? `Insufficient permission: ${message}` : message);
            }
            if (!isLatest(requestId)) return;

            setData(responseData.data || []);
            setColumns(responseData.columns || []);
            setFilterOptions(responseData.filterOptions || null);
            if (!responseData.filterOptions) {
                setBucket('');
                setVendor('');
            } else {
                const nextBucketValues = new Set(
                    (responseData.filterOptions.bucket || []).map(({ value }) => value)
                );
                const nextVendorValues = new Set(
                    (responseData.filterOptions.vendor || []).map(({ value }) => value)
                );
                setBucket((selectedBucket) =>
                    nextBucketValues.has(selectedBucket) ? selectedBucket : '');
                setVendor((selectedVendor) =>
                    nextVendorValues.has(selectedVendor) ? selectedVendor : '');
            }
            setTotalRecords(responseData.totalRecords || 0);
            setTotalPages(
                responseData.totalPages ||
                Math.max(1, Math.ceil((responseData.totalRecords || 0) / PAGE_SIZE)),
            );
        } catch (requestError) {
            if (requestError?.name === 'AbortError') return;
            console.error(requestError);
            if (!isLatest(requestId)) return;
            setError(requestError.message || 'Failed to fetch report data');
            setData([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            if (isLatest(requestId)) setIsLoading(false);
        }
    }, [
        beginRequest,
        buildParams,
        currentPage,
        isLatest,
        reportType,
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [bucket, endDate, reportType, staleHours, startDate, vendor]);

    const handleExportAll = useCallback(async () => {
        const allRows = [];
        let page = 1;
        let totalRecordsToExport = 0;
        let responseColumns;

        for (let iteration = 0; iteration < 50; iteration += 1) {
            const params = buildParams(page, 1000);
            const response = await fetch(`/api/v1/reports/${reportType}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
            });
            const responseData = await response.json().catch(() => ({}));
            if (!response.ok) {
                const message = responseData.error || 'Failed to fetch report data';
                throw new Error(response.status === 403 ? `Insufficient permission: ${message}` : message);
            }

            if (responseColumns === undefined && responseData.columns) {
                responseColumns = responseData.columns;
            }
            const pageData = Array.isArray(responseData.data) ? responseData.data : [];
            allRows.push(...pageData);
            totalRecordsToExport = responseData.totalRecords || 0;

            if (allRows.length >= totalRecordsToExport || pageData.length === 0) break;
            page += 1;
        }

        return { data: allRows, columns: responseColumns || columns };
    }, [buildParams, columns, reportType]);

    const renderRows = () => data.map((row, rowIndex) => (
        <tr
            key={`${row?.payment_id || row?.bill_id || rowIndex}-${rowIndex}`}
            className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
        >
            {columns.map((column) => (
                <td
                    key={column.key}
                    className={`px-6 py-4 text-sm text-gray-900 ${
                        ['currency', 'number'].includes(column.type)
                            ? 'text-right whitespace-nowrap'
                            : 'whitespace-nowrap'
                    }`}
                >
                    {formatCell(row?.[column.key], column.type)}
                </td>
            ))}
        </tr>
    ));

    const filters = (
        <div className="contents">
            <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={(range) => {
                    setDateRange(range);
                    setCurrentPage(1);
                }}
            />
            {[
                ['bucket', hasBucketFilterOptions],
                ['vendor', hasVendorFilterOptions],
            ].filter(([, hasOptions]) => hasOptions).map(([key]) => (
                <select
                    key={key}
                    value={key === 'bucket' ? bucket : vendor}
                    onChange={(event) => {
                        if (key === 'bucket') setBucket(event.target.value);
                        else setVendor(event.target.value);
                        setCurrentPage(1);
                    }}
                    aria-label={key === 'bucket' ? 'Filter by bucket' : 'Filter by vendor'}
                    className="w-40 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                    <option value="">{key === 'bucket' ? 'All buckets' : 'All vendors'}</option>
                    {filterOptions[key].map(({ value, count }) => (
                        <option key={value} value={value}>{`${value} (${count})`}</option>
                    ))}
                </select>
            ))}
            {includeStaleHours && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    Stale after
                    <input
                        type="number"
                        min="1"
                        value={staleHours}
                        onChange={(event) => {
                            setStaleHours(event.target.value);
                            setCurrentPage(1);
                        }}
                        aria-label="Stale after hours"
                        className="w-20 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    hours
                </label>
            )}
        </div>
    );

    const pagination = totalPages > 1 ? (
        <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, totalRecords)} of{' '}
                {totalRecords} results
            </span>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                    Previous
                </button>
                <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                    Next
                </button>
            </div>
        </div>
    ) : null;

    return (
        <ReportTemplate
            title={title}
            reportId={reportType}
            description={description}
            breadcrumbs={[
                { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
                { label: title },
            ]}
            onBack={() => navigate(ROUTES.REPORTS)}
            marginFromSidebar={marginFromSidebar}
            schedulingPreviewParams={{ startDate, endDate }}
            isLoading={isLoading}
            error={error}
            filters={filters}
            data={data}
            columns={columns}
            pagination={pagination}
            emptyMessage={`No rows found in ${title}.`}
            enableSorting={false}
            exportButton={(
                <CSVExportButton
                    data={data}
                    columns={columns}
                    filename={reportType}
                    disabled={isLoading || !totalRecords}
                    onExport={handleExportAll}
                />
            )}
        >
            {renderRows()}
        </ReportTemplate>
    );
};

export default CatalogTableReport;
